import { useEffect, useMemo, useState, useCallback } from "react";
import { api, fmtDate } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Inbox, Zap, Loader2, Repeat, Calendar, X } from "lucide-react";

const ALL = "__all__";

export default function Unscheduled() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [f, setF] = useState({ customer_id: "", site_id: "", month: "", from: "", to: "" });

  const load = useCallback(() => { api.get("/unscheduled").then((r) => setRows(r.data)); }, []);
  useEffect(() => {
    load();
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/sites").then((r) => setSites(r.data)).catch(() => {});
  }, [load]);

  const siteOpts = f.customer_id ? sites.filter((s) => s.customer_id === f.customer_id) : sites;

  const setFilter = (patch) => setF((prev) => {
    const next = { ...prev, ...patch };
    // Month and From/To are alternatives — picking one clears the other
    if (patch.month) { next.from = ""; next.to = ""; }
    if (patch.from !== undefined || patch.to !== undefined) next.month = "";
    // Changing customer clears a site that no longer belongs
    if (patch.customer_id !== undefined && next.site_id) {
      const s = sites.find((x) => x.id === next.site_id);
      if (patch.customer_id && s && s.customer_id !== patch.customer_id) next.site_id = "";
    }
    return next;
  });
  const clearFilters = () => setF({ customer_id: "", site_id: "", month: "", from: "", to: "" });
  const hasFilters = f.customer_id || f.site_id || f.month || f.from || f.to;

  const filtered = useMemo(() => {
    if (!rows) return null;
    return rows.filter((j) => {
      if (f.customer_id && j.customer_id !== f.customer_id) return false;
      if (f.site_id && j.site_id !== f.site_id) return false;
      const d = (j.target_date || "").slice(0, 10);
      if (f.month && d.slice(0, 7) !== f.month) return false;
      if (f.from && (!d || d < f.from)) return false;
      if (f.to && (!d || d > f.to)) return false;
      return true;
    });
  }, [rows, f]);

  const schedule = async (j) => {
    setBusy(j.id);
    try { const { data } = await api.post(`/unscheduled/${j.id}/schedule`, {}); toast.success(`${data.ref} scheduled${data.technician_name ? ` → ${data.technician_name}` : ""} on ${fmtDate(data.scheduled_date)}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(null); }
  };
  const scheduleAll = async () => {
    if (!filtered || filtered.length === 0) return;
    setBusy("all");
    try {
      const body = hasFilters ? { job_ids: filtered.map((j) => j.id) } : {};
      const { data } = await api.post("/unscheduled/auto-schedule-all", body);
      toast.success(`${data.scheduled} job(s) auto-scheduled${hasFilters ? " (filtered)" : ""}`);
      load();
    } finally { setBusy(null); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-md bg-[#0F1725] grid place-items-center text-white"><Inbox className="h-5 w-5" /></div>
          <div><h1 className="text-2xl font-head font-bold text-slate-900">Unscheduled Work</h1>
            <p className="text-sm text-slate-500">Recurring and one-off jobs awaiting a slot</p></div>
        </div>
        <Button className="btn-accent" data-testid="auto-schedule-all" onClick={scheduleAll} disabled={busy === "all" || !filtered || filtered.length === 0}>
          {busy === "all" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Zap className="h-4 w-4 mr-1" />}
          Auto-schedule {hasFilters ? `${filtered?.length ?? 0} shown` : "all"}
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-3 mb-3" data-testid="unscheduled-filters">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
          <div><Label className="text-[11px] text-slate-500">Client</Label>
            <Select value={f.customer_id || ALL} onValueChange={(v) => setFilter({ customer_id: v === ALL ? "" : v })}>
              <SelectTrigger data-testid="filter-customer" className="mt-1 h-9 bg-white"><SelectValue placeholder="All clients" /></SelectTrigger>
              <SelectContent className="bg-white max-h-72">
                <SelectItem value={ALL}>All clients</SelectItem>
                {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div><Label className="text-[11px] text-slate-500">Site</Label>
            <Select value={f.site_id || ALL} onValueChange={(v) => setFilter({ site_id: v === ALL ? "" : v })}>
              <SelectTrigger data-testid="filter-site" className="mt-1 h-9 bg-white"><SelectValue placeholder="All sites" /></SelectTrigger>
              <SelectContent className="bg-white max-h-72">
                <SelectItem value={ALL}>All sites</SelectItem>
                {siteOpts.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div><Label className="text-[11px] text-slate-500">Month</Label>
            <Input type="month" data-testid="filter-month" value={f.month} onChange={(e) => setFilter({ month: e.target.value })} className="mt-1 h-9 bg-white" /></div>
          <div><Label className="text-[11px] text-slate-500">From</Label>
            <Input type="date" data-testid="filter-from" value={f.from} onChange={(e) => setFilter({ from: e.target.value })} className="mt-1 h-9 bg-white" /></div>
          <div><Label className="text-[11px] text-slate-500">To</Label>
            <Input type="date" data-testid="filter-to" value={f.to} onChange={(e) => setFilter({ to: e.target.value })} className="mt-1 h-9 bg-white" /></div>
          <div className="flex items-center gap-2">
            {hasFilters && (
              <Button variant="outline" size="sm" className="h-9" data-testid="filter-clear" onClick={clearFilters}>
                <X className="h-3.5 w-3.5 mr-1" />Clear filters
              </Button>
            )}
          </div>
        </div>
        {rows && (
          <p className="text-xs text-slate-500 mt-2" data-testid="filter-count">
            Showing {filtered?.length ?? 0} of {rows.length}
          </p>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto scroll-thin">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 sticky top-0">
            <tr>{["Job No", "Title", "Customer", "Site", "Type", "Due / Target", "Priority", ""].map((h) => <th key={h} className="text-left font-medium text-slate-500 px-3 py-2.5 whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {filtered === null && <tr><td colSpan={8} className="text-center py-10 text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>}
            {filtered && filtered.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-slate-400">{hasFilters ? "No unscheduled work matches these filters." : "No unscheduled work. Everything has a slot 🎉"}</td></tr>}
            {filtered && filtered.map((j) => (
              <tr key={j.id} data-testid={`unscheduled-row-${j.id}`} className="data-row border-t border-slate-100">
                <td className="px-3 py-2.5 font-medium text-slate-800">{j.ref}</td>
                <td className="px-3 py-2.5 text-slate-700">
                  <span className="inline-flex items-center gap-1.5">{j.recurring && <Repeat className="h-3.5 w-3.5 accent-text" title="Recurring" />}{j.title}</span>
                </td>
                <td className="px-3 py-2.5 text-slate-600">{j.customer_name}</td>
                <td className="px-3 py-2.5 text-slate-600">{j.site_name}</td>
                <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{j.job_type}</td>
                <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap"><Calendar className="h-3.5 w-3.5 inline mr-1 text-slate-400" />{fmtDate(j.target_date)}</td>
                <td className="px-3 py-2.5"><StatusBadge status={j.priority} /></td>
                <td className="px-3 py-2.5 text-right">
                  <Button size="sm" variant="outline" data-testid={`schedule-btn-${j.id}`} onClick={() => schedule(j)} disabled={busy === j.id}>
                    {busy === j.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Auto-schedule"}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
